import type { UserPublic } from "shared/schemas";

/**
 * `useCurrentUser().data` has THREE states, and `!user` collapses two of them
 * that mean opposite things:
 *
 *   - a `UserPublic`  -> signed in
 *   - `null`          -> signed out. ONLY produced by a clean 401.
 *   - `undefined`     -> we do not know. Either the request is still in flight,
 *                        or it FAILED - a 500, a dropped connection, a parse
 *                        error. `isLoading` is false in that second case, so
 *                        guarding on `isLoading` alone does not separate them.
 *
 * Treating `undefined` as signed out is a recurring bug here: it shows a
 * signed-in customer the signed-out flow the moment one request hiccups. These
 * predicates exist so a call site says which of the three it means instead of
 * reaching for `!user` and getting two.
 *
 * They live in their own module rather than next to the hook, and that is a
 * choice rather than a necessity - worth stating plainly, because the first
 * version of this comment implied there was no alternative.
 *
 * The pressure is real: eight test files mock `hooks/use-auth.js` with a
 * literal factory, and a factory mock is a whitelist, so a pure helper added
 * there has to be re-declared in each of them and in every future one, or the
 * test dies with "no export defined". That happened, eightfold, on the first
 * attempt. But `vi.mock(path, async (importOriginal) => ({ ...(await
 * importOriginal()), useCurrentUser: vi.fn() }))` is the standard way out and
 * would have kept them next to the hook.
 *
 * The separate module was preferred anyway for a smaller reason: these are
 * pure functions with no I/O, and putting them where a mock cannot reach means
 * a test exercises the real ones without anybody having to remember to spread
 * the original module in.
 */
export function isSignedOut(user: UserPublic | null | undefined): user is null {
  return user === null;
}

/** True while the answer is not known - in flight OR failed. See isSignedOut. */
export function isAuthUnknown(user: UserPublic | null | undefined): user is undefined {
  return user === undefined;
}
